import { Button, Callout, EmptyState, Spinner, Tag } from '@humanforce/mango-ui-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import type { BsbValidationDetails, BsbValidationFinding } from './bsbValidationApiTypes';
import { isForbidden, isUnauthenticated, overrideBsbFinding } from './bsbValidationHttpClient';
import {
  bankingFindingLabel,
  hasDirectoryEvidence,
  runLevelFindings,
  summarisePayRunBanking,
  workerFindings,
} from './payRunBankingModel';
import { PayRunBankingOverrideModal } from './PayRunBankingOverrideModal';

/** Anchor the Review timeline action scrolls to when it routes the operator here. */
export const PAY_RUN_BANKING_SECTION_ID = 'pay-run-banking-verification';

const directoryDate = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney',
});

const formatDirectoryAsAt = (value: string): string | null => {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return directoryDate.format(new Date(parsed));
};

interface PayRunBankingPanelProps {
  businessRecordId: string;
  payId: string;
  /** Owned by the Pay Run detail screen so the timeline and this panel can never disagree. */
  details: BsbValidationDetails | null;
  loading: boolean;
  /** Set only for a genuine read failure; a 404 is reported through `notEvaluated` instead. */
  error: string | null;
  notEvaluated: boolean;
  /** Re-reads the shared Banking evidence. Used by Retry and after a successful override. */
  onReload: () => Promise<void> | void;
  /** Lets the Pay Run surface refresh its own state after a successful override. */
  onOverrideApplied?: () => Promise<void> | void;
  /** Reuses the Pay Run feature's existing inline notification pattern rather than a toast. */
  onNotice?: (notice: { tone: 'positive' | 'critical'; title: string; message: string }) => void;
  /** Renders the sibling Compliance content while this panel keeps the audited override workflow in one owner. */
  renderComplianceContent?: (context: {
    findings: BsbValidationFinding[];
    onOverride: (finding: BsbValidationFinding) => void;
  }) => ReactNode;
}

/**
 * Banking (BSB) verification section for the Pay Run Compliance tab. Presentational over the PRR-309 contract that
 * the detail screen owns, plus the existing per-finding override action. Contains no role, claim or permission
 * logic: authorization stays with the backend, and a 401/403 is reported to the operator as-is.
 */
export const PayRunBankingPanel = ({
  businessRecordId,
  payId,
  details,
  loading,
  error,
  notEvaluated,
  onReload,
  onOverrideApplied,
  onNotice,
  renderComplianceContent,
}: PayRunBankingPanelProps) => {
  const [overrideFinding, setOverrideFinding] = useState<BsbValidationFinding | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [overrideBusy, setOverrideBusy] = useState(false);
  // Synchronous re-entrancy guard. `overrideBusy` only disables the button after React re-renders, which leaves a
  // window in which two near-simultaneous confirmations both pass. An override is an immutable, one-shot audit
  // record, so the second must be refused before it can reach the network.
  const overrideInFlight = useRef(false);

  const summary = useMemo(
    () => summarisePayRunBanking({ details, loading, failed: error !== null }),
    [details, loading, error],
  );
  const workers = useMemo(() => workerFindings(details), [details]);
  const runLevel = useMemo(() => runLevelFindings(details), [details]);

  const closeOverride = useCallback(() => {
    setOverrideFinding(null);
    setReason('');
    setReasonError(null);
    setOverrideError(null);
  }, []);

  const openOverride = useCallback((finding: BsbValidationFinding) => {
    setOverrideFinding(finding);
    setReason('');
    setReasonError(null);
    setOverrideError(null);
  }, []);

  const confirmOverride = useCallback(async () => {
    if (!overrideFinding) return;
    if (overrideInFlight.current) return;
    if (reason.trim().length === 0) {
      setReasonError('Enter a reason for this override.');
      return;
    }
    overrideInFlight.current = true;
    setReasonError(null);
    setOverrideError(null);
    setOverrideBusy(true);
    try {
      await overrideBsbFinding(businessRecordId, payId, overrideFinding.findingId, reason);
    } catch (failure) {
      setOverrideError(isForbidden(failure)
        ? 'You are not authorised to override this finding. The finding has not been changed.'
        : isUnauthenticated(failure)
          ? 'Your session has expired. Sign in again and retry. The finding has not been changed.'
          : failure instanceof Error
            ? failure.message
            : 'The override could not be completed. The finding has not been changed.');
      setOverrideBusy(false);
      overrideInFlight.current = false;
      return;
    }
    // The write has succeeded. Refresh failures below must never be reported as override failures, because the
    // override is an immutable audit record and a false failure invites the operator to retry it.
    setOverrideBusy(false);
    overrideInFlight.current = false;
    closeOverride();
    onNotice?.({
      tone: 'positive',
      title: 'Banking finding overridden',
      message: 'The reason has been recorded for audit.',
    });
    try {
      // One refresh, not two: onOverrideApplied re-reads the Pay Run surface and, through the readiness reload
      // key, the shared Banking evidence as well. onReload is the direct path for callers without that wiring.
      await (onOverrideApplied ? onOverrideApplied() : onReload());
    } catch {
      // Deliberately NOT titled as an override failure: the override succeeded and is not reversible, so a
      // failure headline here would invite the operator to retry it.
      onNotice?.({
        tone: 'critical',
        title: 'Banking evidence could not be refreshed',
        message: 'The override was recorded. Reload the Pay Run to see current banking evidence.',
      });
    }
  }, [businessRecordId, closeOverride, onNotice, onOverrideApplied, onReload, overrideFinding, payId, reason]);

  const asAt = details && hasDirectoryEvidence(details) ? formatDirectoryAsAt(details.directoryCompletedAt) : null;

  return (
    <div data-testid="pay-run-banking-panel" style={{ display: 'contents' }}>
    <section
      id={PAY_RUN_BANKING_SECTION_ID}
      tabIndex={-1}
      aria-labelledby="pay-run-banking-heading"
      className="mgo-stack mgo-gap-l"
      aria-busy={loading}
    >
      <div className="mgo-stack mgo-gap-xs">
        <h3 id="pay-run-banking-heading" className="mgo-text-l mgo-weight-strong">Banking (BSB) verification</h3>
        <p className="mgo-text-s mgo-color-muted">
          Pre-release BSB checks against the shared bank directory. Overriding a finding records an audit entry and never
          changes or reroutes a payment.
        </p>
      </div>

      {loading && !details ? (
        <div className="mgo-cluster mgo-gap-s">
          <Spinner />
          <span className="mgo-text-s mgo-color-muted">Checking banking details…</span>
        </div>
      ) : error && !details ? (
        <Callout
          tone="critical"
          title="Banking verification could not be loaded"
          action={<Button label="Retry" variant="secondary" size="small" onClick={() => void onReload()} />}
        >
          {error}
        </Callout>
      ) : notEvaluated && !details ? (
        <EmptyState
          title="Not yet evaluated"
          description="Banking (BSB) verification runs as part of the readiness evaluation for this Pay Run."
        />
      ) : (
        <>
          {error ? (
            <Callout
              tone="caution"
              title="Showing the last known banking evidence"
              action={<Button label="Retry" variant="secondary" size="small" onClick={() => void onReload()} />}
            >
              {error}
            </Callout>
          ) : null}
          <div className="mgo-cluster mgo-gap-s" data-testid="pay-run-banking-counts">
            <Tag label={`${summary.blockedCount} blocked`} tone={summary.blockedCount > 0 ? 'critical' : 'neutral'} />
            <Tag label={`${summary.attentionCount} to review`} tone={summary.attentionCount > 0 ? 'caution' : 'neutral'} />
            <Tag label={`${summary.overriddenCount} overridden`} tone="neutral" />
            {asAt ? (
              <span className="mgo-text-s mgo-color-muted">
                Directory as at {asAt} · refreshed every {details?.refreshCadenceHours} hours
              </span>
            ) : (
              <span className="mgo-text-s mgo-color-muted">Directory reference unavailable for this validation</span>
            )}
          </div>

          {runLevel.map((finding) => (
            <Callout
              key={finding.findingId}
              tone={finding.severity === 'blocker' ? 'critical' : 'caution'}
              title={bankingFindingLabel(finding.ruleCode)}
            >
              <span className="mgo-stack mgo-gap-s">
                <span>
                  {finding.ruleCode === 'BsbDirectoryUnavailable'
                    ? 'The bank directory could not be read, so banking cannot be verified. Release stays blocked until it is available again.'
                    : asAt
                      ? `The bank directory was last refreshed on ${asAt}, which is older than the configured ${details?.refreshCadenceHours}-hour cadence.`
                      : `The bank directory is older than the configured ${details?.refreshCadenceHours}-hour refresh cadence.`}
                  {finding.overridden ? ' This finding has been overridden.' : ''}
                </span>
                {finding.overrideAllowed && !finding.overridden ? (
                  <span>
                    <Button
                      label="Override"
                      variant="secondary"
                      size="small"
                      onClick={() => openOverride(finding)}
                    />
                  </span>
                ) : null}
              </span>
            </Callout>
          ))}

        </>
      )}

      <PayRunBankingOverrideModal
        finding={overrideFinding}
        reason={reason}
        busy={overrideBusy}
        error={overrideError}
        reasonError={reasonError}
        onReasonChange={setReason}
        onConfirm={() => void confirmOverride()}
        onClose={closeOverride}
      />
    </section>
    {renderComplianceContent?.({ findings: workers, onOverride: openOverride })}
    </div>
  );
};
